"use client"

/**
 * Velova landing motion primitives.
 *
 * Lightweight, reduced-motion aware framer-motion building blocks used across
 * the Velova marketing surfaces. Composable enough to keep page.tsx tidy.
 */

import {
  motion,
  useInView,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
  AnimatePresence,
  type MotionProps,
  type Variants,
} from "framer-motion"
import {
  forwardRef,
  Fragment,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type HTMLAttributes,
  type ReactNode,
} from "react"

/* ─────────────────────────────────────────────────────────────
   FadeUp — element rises + fades when entering viewport.
   ───────────────────────────────────────────────────────────── */
type FadeUpProps = {
  as?: ElementType
  delay?: number
  y?: number
  once?: boolean
  amount?: number
  className?: string
  children: ReactNode
} & MotionProps

export function FadeUp({
  as = "div",
  delay = 0,
  y = 22,
  once = true,
  amount = 0.25,
  className,
  children,
  ...rest
}: FadeUpProps) {
  const reduce = useReducedMotion()
  const Comp = (motion as any)[as as string] ?? motion.div
  if (reduce) {
    return (
      <Comp className={className} {...rest}>
        {children}
      </Comp>
    )
  }
  return (
    <Comp
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount }}
      transition={{ duration: 0.85, delay, ease: [0.2, 0.8, 0.2, 1] }}
      className={className}
      {...rest}
    >
      {children}
    </Comp>
  )
}

/* ─────────────────────────────────────────────────────────────
   Stagger — container/item pair for orchestrated reveals.
   ───────────────────────────────────────────────────────────── */
const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.085, delayChildren: 0.05 },
  },
}

const staggerItem: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.2, 0.8, 0.2, 1] },
  },
}

export function Stagger({
  className,
  children,
  amount = 0.2,
  once = true,
}: {
  className?: string
  children: ReactNode
  amount?: number
  once?: boolean
}) {
  const reduce = useReducedMotion()
  if (reduce) {
    return <div className={className}>{children}</div>
  }
  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      whileInView="show"
      viewport={{ once, amount }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

export function StaggerItem({
  className,
  children,
  as = "div",
  y = 24,
  duration = 0.8,
}: {
  className?: string
  children: ReactNode
  as?: ElementType
  /** Entrance travel distance/speed override — defaults match the shared staggerItem variants. */
  y?: number
  duration?: number
}) {
  const reduce = useReducedMotion()
  const Comp = (motion as any)[as as string] ?? motion.div
  if (reduce) {
    return <Comp className={className}>{children}</Comp>
  }
  const variants: Variants =
    y === 24 && duration === 0.8
      ? staggerItem
      : {
          hidden: { opacity: 0, y },
          show: { opacity: 1, y: 0, transition: { duration, ease: [0.2, 0.8, 0.2, 1] } },
        }
  return (
    <Comp variants={variants} className={className}>
      {children}
    </Comp>
  )
}

/* ─────────────────────────────────────────────────────────────
   AnimatedNumber — counts to a numeric target on scroll.
   Supports prefix/suffix (e.g. "$", "%", "M", "h").
   ───────────────────────────────────────────────────────────── */
export function AnimatedNumber({
  value,
  prefix = "",
  suffix = "",
  decimals = 0,
  duration = 1.6,
  className,
}: {
  value: number
  prefix?: string
  suffix?: string
  decimals?: number
  duration?: number
  className?: string
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref, { once: true, amount: 0.5 })
  const [display, setDisplay] = useState("0")

  useEffect(() => {
    if (!inView) return
    let raf = 0
    const start = performance.now()
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / (duration * 1000))
      const eased = 1 - Math.pow(1 - p, 3)
      const v = value * eased
      setDisplay(
        v.toLocaleString(undefined, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }),
      )
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [inView, value, duration, decimals])

  return (
    <span ref={ref} className={className} aria-label={`${prefix}${value}${suffix}`}>
      {prefix}
      {display}
      {suffix}
    </span>
  )
}

/* ─────────────────────────────────────────────────────────────
   WordRotate — rotates through a list of words in place.
   ───────────────────────────────────────────────────────────── */
export function WordRotate({
  words,
  interval = 2400,
  className,
  style,
}: {
  words: string[]
  interval?: number
  className?: string
  style?: CSSProperties
}) {
  const reduce = useReducedMotion()
  const [i, setI] = useState(0)
  useEffect(() => {
    if (reduce) return
    const id = setInterval(() => setI((x) => (x + 1) % words.length), interval)
    return () => clearInterval(id)
  }, [words.length, interval, reduce])

  if (reduce) {
    return (
      <span className={className} style={style}>
        {words[0]}
      </span>
    )
  }

  return (
    <span
      className={className}
      style={{
        display: "inline-grid",
        gridTemplateColumns: "1fr",
        gridTemplateRows: "1fr",
        overflow: "hidden",
        verticalAlign: "bottom",
        paddingRight: "0.12em",
        ...style
      }}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={words[i]}
          initial={{ y: "115%", opacity: 0, rotate: 2 }}
          animate={{ y: 0, opacity: 1, rotate: 0 }}
          exit={{ y: "-115%", opacity: 0, rotate: -2 }}
          transition={{ duration: 0.55, ease: [0.2, 0.8, 0.2, 1] }}
          style={{ gridArea: "1 / 1 / 2 / 2", display: "inline-block" }}
        >
          {words[i]}
        </motion.span>
      </AnimatePresence>
    </span>
  )
}

/* ─────────────────────────────────────────────────────────────
   MagneticBtn — wrapper that pulls children toward the cursor.
   ───────────────────────────────────────────────────────────── */
export const MagneticBtn = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & { strength?: number }
>(function MagneticBtn({ children, className, strength = 14, ...rest }, ref) {
  const reduce = useReducedMotion()
  const inner = useRef<HTMLDivElement>(null)
  const x = useSpring(useMotionValue(0), { stiffness: 220, damping: 18, mass: 0.5 })
  const y = useSpring(useMotionValue(0), { stiffness: 220, damping: 18, mass: 0.5 })

  if (reduce) {
    return (
      <div ref={ref} className={className} {...rest}>
        {children}
      </div>
    )
  }

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!inner.current) return
    const r = inner.current.getBoundingClientRect()
    const px = (e.clientX - r.left - r.width / 2) / r.width
    const py = (e.clientY - r.top - r.height / 2) / r.height
    x.set(px * strength)
    y.set(py * strength)
  }
  const handleLeave = () => {
    x.set(0)
    y.set(0)
  }

  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className={className}
      {...rest}
    >
      <motion.div ref={inner} style={{ x, y, display: "inline-block" }}>
        {children}
      </motion.div>
    </div>
  )
})

/* ─────────────────────────────────────────────────────────────
   ScrollProgress — slim ink bar fixed to the top.
   Fades out while a pinned scroll-story section is active so the
   bar doesn't compete with the section's own internal progress.
   Sections opt in by adding `data-pin-progress` attribute.
   ───────────────────────────────────────────────────────────── */
export function ScrollProgress() {
  const { scrollYProgress } = useScroll()
  // Clamp raw scrollYProgress before handing it to useSpring — during
  // hydration the page-level scrollYProgress can emit values outside [0,1]
  // (negative when rubber-banding on iOS, >1 briefly during layout shifts),
  // which propagates into scaleX and explodes Web Animations keyframes.
  const safe = useTransform(scrollYProgress, (raw) =>
    typeof raw === "number" && isFinite(raw) ? Math.max(0, Math.min(1, raw)) : 0,
  )
  const w = useSpring(safe, { stiffness: 120, damping: 22, mass: 0.4 })
  const [pinned, setPinned] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined") return
    const check = () => {
      const sections = document.querySelectorAll<HTMLElement>("[data-pin-progress]")
      let inside = false
      sections.forEach((s) => {
        const r = s.getBoundingClientRect()
        if (r.top <= 0 && r.bottom > window.innerHeight * 0.4) inside = true
      })
      setPinned(inside)
    }
    check()
    window.addEventListener("scroll", check, { passive: true })
    window.addEventListener("resize", check)
    return () => {
      window.removeEventListener("scroll", check)
      window.removeEventListener("resize", check)
    }
  }, [])

  return (
    <motion.div
      aria-hidden
      animate={{ opacity: pinned ? 0 : 1 }}
      transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
      style={{ scaleX: w, transformOrigin: "0% 50%" }}
      className="fixed left-0 right-0 top-0 z-[60] h-[2px] bg-[var(--velova-ink)]"
    />
  )
}

/* ─────────────────────────────────────────────────────────────
   ParallaxY — translates child along Y as it scrolls past.
   ───────────────────────────────────────────────────────────── */
export function ParallaxY({
  children,
  range = 60,
  className,
}: {
  children: ReactNode
  range?: number
  className?: string
}) {
  // Outer div: receives the caller's className (typically absolute/positioning).
  // Inner div: stable, relative-positioned, used as the scroll target so framer
  // motion's getComputedStyle check always passes regardless of CSS load order.
  //
  // The ref-bearing inner div is always rendered (same DOM shape whether or
  // not motion is reduced) — useScroll's target ref must hydrate on the very
  // first render. It previously only appeared in the non-reduced branch,
  // which crashed the whole page ("Target ref is defined but not hydrated")
  // whenever prefers-reduced-motion is true from initial load, since
  // useReducedMotion() can resolve to true before ref.current ever attaches.
  const reduce = useReducedMotion()
  const ref = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  })
  // Guard against NaN/Infinity during SSR hydration or when the target
  // element hasn't laid out yet — framer-motion crashes the Web Animations
  // API with "Offsets must be null or in the range [0,1]" otherwise.
  const y = useTransform(scrollYProgress, (raw) => {
    const v = typeof raw === "number" && isFinite(raw) ? Math.max(0, Math.min(1, raw)) : 0
    return range + (-range - range) * v
  })
  return (
    <div className={className}>
      <div ref={ref} style={{ position: "relative", height: "100%", width: "100%" }}>
        {reduce ? (
          <div style={{ height: "100%", width: "100%" }}>{children}</div>
        ) : (
          <motion.div style={{ y, height: "100%", width: "100%" }}>{children}</motion.div>
        )}
      </div>
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────
   BorderBeam — animated luminous bead chasing the border.
   Pure CSS-mask + motion.
   ───────────────────────────────────────────────────────────── */
export function BorderBeam({
  duration = 9,
  size = 220,
  color = "#6BB4F2",
  delay = 0,
  className,
}: {
  duration?: number
  size?: number
  color?: string
  delay?: number
  className?: string
}) {
  const reduce = useReducedMotion()
  if (reduce) return null
  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute inset-0 rounded-[inherit] [mask:linear-gradient(transparent,transparent),linear-gradient(#000,#000)] [mask-clip:padding-box,border-box] [mask-composite:intersect] ${className ?? ""}`}
      style={{ border: "1px solid transparent" }}
    >
      <motion.div
        className="absolute aspect-square rounded-full"
        style={{
          width: size,
          height: size,
          background: `radial-gradient(circle, ${color} 0%, ${color}88 30%, transparent 70%)`,
          offsetPath: `rect(0 100% 100% 0 round 1px)`,
        }}
        initial={{ offsetDistance: "0%" }}
        animate={{ offsetDistance: "100%" }}
        transition={{ duration, repeat: Infinity, ease: "linear", delay }}
      />
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────
   TextReveal — masks line, reveals when section enters viewport.
   Uses useInView on a wrapper so the trigger is reliable even when
   the motion.span starts outside its parent's clip box.
   ───────────────────────────────────────────────────────────── */
export function TextReveal({
  children,
  delay = 0,
  className,
}: {
  children: ReactNode
  delay?: number
  className?: string
}) {
  const reduce = useReducedMotion()
  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref, { once: true, amount: 0.1, margin: "0px 0px -10% 0px" })
  if (reduce) {
    return <span className={className}>{children}</span>
  }
  return (
    <span
      ref={ref}
      className={className}
      style={{ display: "inline-block", overflow: "hidden", verticalAlign: "bottom" }}
    >
      <motion.span
        style={{ display: "inline-block" }}
        initial={{ y: "110%" }}
        animate={inView ? { y: "0%" } : { y: "110%" }}
        transition={{ duration: 0.95, ease: [0.2, 0.8, 0.2, 1], delay }}
      >
        {children}
      </motion.span>
    </span>
  )
}

/* ─────────────────────────────────────────────────────────────
   WordReveal — masked per-word reveal: each word rises out of its
   own overflow-hidden clip, staggered, when the text scrolls into
   view. A more granular alternative to TextReveal (which reveals
   one whole line at a time) for headlines that want to read as
   assembling word by word.
   ───────────────────────────────────────────────────────────── */
export function WordReveal({
  text,
  as = "span",
  className,
  stagger = 0.09,
  duration = 0.6,
  distance = 40,
  startDelay = 0,
  once = true,
  amount = 0.3,
}: {
  text: string
  as?: ElementType
  className?: string
  stagger?: number
  duration?: number
  distance?: number
  startDelay?: number
  once?: boolean
  amount?: number
}) {
  const reduce = useReducedMotion()
  const words = text.split(" ")
  const Comp = (motion as any)[as as string] ?? motion.span
  if (reduce) {
    return <Comp className={className}>{text}</Comp>
  }
  return (
    <Comp className={className}>
      {words.map((w, i) => (
        <Fragment key={i}>
          <span
            style={{
              display: "inline-block",
              overflow: "hidden",
              verticalAlign: "bottom",
              paddingBottom: "0.08em",
            }}
          >
            <motion.span
              style={{ display: "inline-block" }}
              initial={{ opacity: 0, y: distance }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once, amount }}
              transition={{ duration, delay: startDelay + i * stagger, ease: [0.33, 1, 0.68, 1] }}
            >
              {w}
            </motion.span>
          </span>
          {i < words.length - 1 ? " " : ""}
        </Fragment>
      ))}
    </Comp>
  )
}
